<template>
  <div class="p-4 bg-white rounded-lg">
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-xl font-semibold">Email Sales Order - {{ salesOrderNumber }}</h2>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Address</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="recipientEmail" type="email"
             placeholder="Enter recipient email address"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Subject</label>
      <input class="bge-input bge-input-spacing rounded w-full" v-model="emailSubject" type="text"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Email Text</label>
      <StyledTextEditor v-model="emailMessage" :options="editorOptions"/>
    </div>

    <div class="mb-4">
      <label class="block uppercase font-semibold text-sm">Send Copy To (Sent as separate email)</label>
      <Chips v-model="ccEmail" class="w-full" separator=","/>
    </div>

    <div class="mb-4 flex items-center">
      <Checkbox v-model="hasAttachments" :binary="true" class="mr-2"/>
      <label class="block uppercase font-semibold text-sm">Include 'SO' Attachment</label>
    </div>

    <div class="flex justify-end space-x-2">
      <AppButton color="gray" @click="setAsDefault" label="Set as Default"></AppButton>
      <AppButton color="blue" @click="sendOrder" label="Send Order">
        <template v-slot:icon>
          <Icon iconType="send"/>
        </template>
      </AppButton>
    </div>

    <div class="mt-10">
      <EmailQueueTable ref="emailQueueTable" :referenceId="this.salesOrderId"></EmailQueueTable>
    </div>
  </div>
</template>

<script>
import AppButton from "@/components/Button.vue";
import Icon from "@/components/Icon.vue";
import StyledTextEditor from "@/components/StyledTextEditor.vue";
import notificationMixin from "@/mixins/notificationMixin";
import EmailQueueTable from "@/views/HR/Sales/Components/EmailQueueTable.vue";

export default {
  name: "EmailSendSalesOrder",
  mixins: [notificationMixin],
  components: {
    Icon,
    AppButton,
    EmailQueueTable,
    StyledTextEditor
  },
  props: {
    salesOrderId: {
      type: String,
      required: true,
    },
    salesOrderNumber: {
      type: String,
      required: true,
    },
    customer: {
      type: Object,
      required: true,
    },
    salesOrder: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      recipientEmail: '',
      emailSubject: '',
      emailMessage: '',
      ccEmail: null,
      defaultSubject: '',
      defaultEmailText: '',
      hasAttachments: true,
      attachmentUrls: '',
      editorOptions: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{'list': 'ordered'}, {'list': 'bullet'}],
        ]
      },
    };
  },
  mounted() {
    this.fetchEmailTemplate();
    this.recipientEmail = this.customer.people.email;
  },
  methods: {
    replacePlaceholders(template, data) {
      return template.replace(/{{\s*(\w+)\s*}}/g, (match, p1) => data[p1] || '');
    },
    async fetchEmailTemplate() {
      try {
        const template = await this.EmailService.getTemplateById('emt_a5a528e374614eb89f55745ca8819516');
        this.defaultSubject = this.replacePlaceholders(template.subject || '', {
          orderNumber: this.salesOrderNumber
        });
        this.defaultEmailText = this.replacePlaceholders(template.body || '', {
          customer: `${this.customer.people.firstname || ''} ${this.customer.people.lastname || ''}`,
          orderNumber: this.salesOrderNumber,
          deliveryDate: this.salesOrder.requested_delivery_date || ''
        });

        this.emailSubject = this.defaultSubject;
        this.emailMessage = this.defaultEmailText;

      } catch (error) {
        console.error('Error fetching email template:', error);
      }
    },
    setAsDefault() {
      this.emailSubject = this.defaultSubject;
      this.emailMessage = this.defaultEmailText;
    },
    async sendOrder() {
      const emailData = {
        template_key: 'sales_order_email_temp', // Updated key for sales order
        reference_id: this.salesOrderId,
        to: [
          {
            name: `${this.customer.people.firstname || 'Hi'} ${this.customer.people.lastname || ''}`,
            email: this.recipientEmail
          }
        ],
        cc: this.ccEmail ? this.ccEmail : [],
        bcc: [],
        status: "PENDING",
        category: "notification",
        data: {
          customer: this.customer.people.name,
          orderNumber: this.salesOrderNumber,
          deliveryDate: this.salesOrder.requested_delivery_date || '',
        },
        use_custom_subject_body: true,
        custom_subject: this.emailSubject,
        custom_body: this.emailMessage,
        has_attachments: this.hasAttachments,
        attachments: this.hasAttachments ? [
          {
            url: `sales-orders/${this.salesOrderId}/report/sales-order`,
            file_name: `${this.salesOrderNumber}-sales-order.pdf`
          }
        ] : []
      };

      try {
        await this.EmailService.createQueueItem(emailData);
        this.notifySuccess('Email sent successfully', 'success');
        await this.$refs.emailQueueTable.loadEmailQueues();
      } catch (error) {
        console.error('Error sending email:', error);
        this.notifyError('Failed to send email', 'error');
      }
    }
  },
};
</script>

<style scoped>
/* No additional styles required */
</style>
